// Home.js
import { useContext } from 'react'
import { Container, Row, Col, Button, Card } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBoxesStacked,
  faChartLine,
  faCreditCard,
  faQrcode
} from '@fortawesome/free-solid-svg-icons'
import logo from './poslogo.png'
import { ThemeContext } from '../components/ThemeContext.js'

const features = [
  {
    icon: faBoxesStacked,
    title: 'Know what’s in stock',
    description:
      'Keep products, quantities, and low-stock levels organized in one place.'
  },
  {
    icon: faCreditCard,
    title: 'Check out with confidence',
    description:
      'A practical point of sale keeps your products and checkout together.'
  },
  {
    icon: faQrcode,
    title: 'Send electronic receipts',
    description:
      'Offer customers a convenient electronic receipt with a QR scan at checkout.'
  },
  {
    icon: faChartLine,
    title: 'See how business is doing',
    description:
      'Use clear inventory and sales reports to understand the day-to-day.'
  }
]

const Home = () => {
  const { theme } = useContext(ThemeContext)

  const pageStyle = {
    color: theme.text,
    '--home-card': theme.card,
    '--home-text': theme.text,
    '--home-muted': theme.subtext,
    '--home-accent': theme.sidebarAccent,
    '--home-surface': theme.surfaceMuted,
    '--home-border': theme.surfaceMuted
  }

  return (
    <Container className='home-page' style={pageStyle}>
      <section className='home-hero'>
        <Row className='align-items-center g-4'>
          <Col lg={7}>
            <span className='home-eyebrow'>A simpler way to run your shop</span>
            <h1>Inventory and checkout, made simpler.</h1>
            <p className='home-hero-copy'>
              GeraniumPoS helps small businesses stay on top of their stock,
              serve customers, and make every sale count.
            </p>
            <div className='home-hero-actions'>
              <Button
                as={Link}
                to='/owner'
                variant='primary'
                size='lg'
                className='home-primary-action'
              >
                Register your business
              </Button>
              <Button
                as={Link}
                to='/owner/login'
                variant='outline-primary'
                size='lg'
                className='home-secondary-action'
              >
                Owner login
              </Button>
            </div>
            <p className='home-hero-note'>
              Built for sole proprietors and small businesses.
            </p>
          </Col>
          <Col lg={5}>
            <div className='home-brand-visual'>
              <div className='home-brand-badge'>
                <span aria-hidden='true'>🌿</span> GeraniumPoS
              </div>
              <img
                src={logo}
                alt='GeraniumPoS product logo'
                className='home-logo-image'
              />
              <div className='home-visual-caption'>
                <span className='home-visual-dot' aria-hidden='true' />
                Inventory, sales, and receipts in one place
              </div>
            </div>
          </Col>
        </Row>
      </section>

      <section className='home-features' aria-labelledby='home-features-title'>
        <div className='home-section-heading'>
          <span className='home-eyebrow'>Everything connected</span>
          <h2 id='home-features-title'>The essentials for everyday business</h2>
          <p>From the stockroom to the customer’s receipt.</p>
        </div>
        <Row className='g-3'>
          {features.map((feature) => (
            <Col key={feature.title} xs={12} sm={6} xl={3}>
              <Card className='home-feature-card h-100'>
                <Card.Body>
                  <span className='home-feature-icon' aria-hidden='true'>
                    <FontAwesomeIcon icon={feature.icon} />
                  </span>
                  <Card.Title>{feature.title}</Card.Title>
                  <Card.Text>{feature.description}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </section>

      <section className='home-receipt-callout'>
        <div>
          <span className='home-eyebrow'>A smoother finish to every sale</span>
          <h2>Receipts customers can take with them.</h2>
          <p>
            Let customers scan a QR code at checkout to receive an electronic
            receipt in their GlobalReceipt.ca wallet.
          </p>
        </div>
        <FontAwesomeIcon
          icon={faQrcode}
          className='home-receipt-icon'
          aria-hidden='true'
        />
      </section>

      <section className='home-bottom-cta'>
        <div>
          <h2>Ready to get organized?</h2>
          <p>
            Set up your business and bring your inventory and sales together.
          </p>
        </div>
        <Button
          as={Link}
          to='/owner'
          variant='primary'
          className='home-primary-action'
        >
          Get started
        </Button>
      </section>

      <footer className='home-contact'>
        <span>
          Questions? <a href='mailto:info@hermanosgazpachos.ca'>Contact us</a>
        </span>
        <a
          href='https://globalreceipt.ca'
          target='_blank'
          rel='noopener noreferrer'
        >
          Learn about GlobalReceipt
        </a>
      </footer>
    </Container>
  )
}

export default Home
